import { PageProps } from 'gatsby';
import React, { useEffect } from 'react';

import styled from '@emotion/styled';
import { Box, Container, Stack, Typography } from '@mui/material';
import { AnalyticsPageType } from '@shopify/hydrogen-react';
import {
    useContentfulInspectorMode, useContentfulLiveUpdates
} from '../../../../shared/hooks/contentful';

import Fallback from '../../../../components/fallback';
import { HorizontalSlantContainer } from '../../../../components/horizontal-slant-container';
import * as sections from '../../../../components/sections';
import { useAnalytics } from '../../../../shared/analytics';
import { dynamicHeroHeight } from '../../../../shared/styles/dynamicHeroHeight';
import { headerSlantContainerNoContent } from '../../../../shared/styles/headerSlantContainer';

type ContentfulPageContentProps = (PageProps<Queries.HomepagePageContentQuery | Queries.ContentfulPageContentQuery>) & {
    showTitle?: boolean
}

const smallHeroBreakpointHeight = 600
const smallHeroMinHeight = 600
const HeightDivSmall = styled.div`
${dynamicHeroHeight({
    breakpointHeight: smallHeroBreakpointHeight,
    minHeroHeight: smallHeroMinHeight,
})}`

const HeightDiv = styled.div`
${dynamicHeroHeight({})}
`

export function ContentfulPageContent({ showTitle, ...props }: ContentfulPageContentProps) {
    const pageDataProps = (props.data as Queries.HomepagePageContentQuery).pageData
    const headerSlantContainerNoContentStyles = headerSlantContainerNoContent();

    const isWholesalePage = props.location.pathname === '/wholesale/'

    // console.log('heree', props)

    const inspectorProps = useContentfulInspectorMode();
    const pageData = useContentfulLiveUpdates({
        ...pageDataProps,
        sys: { id: pageDataProps.contentful_id },
    });

    const analytics = useAnalytics();
    useEffect(() => {
        analytics.trackPageView(AnalyticsPageType.page);
    }, [])

    const firstBlockComponent = () => {
        if (pageData.blocks.length < 1) return <></>

        // console.log('heree block1', pageData.blocks[0])

        const { id, blockType, ...componentProps } = pageData.blocks[0]
        const Component = sections[blockType] || Fallback
        return (
            <Component key={id} {...(componentProps as any)} isFirstHero={true} />
        )
    }

    const secondBlockComponent = () => {
        if (pageData.blocks.length < 2) return <></>

        const { id, blockType, ...componentProps } = pageData.blocks[1]
        const Component = sections[blockType] || Fallback

        if ((blockType as any) === 'ContentfulRichTextBlock' && (componentProps as any).body) {
            return <Container maxWidth="lg"><Component key={id} {...(componentProps as any)} aboveFold={true} /></Container>
        }
        return <Component key={id} {...(componentProps as any)} aboveFold={true} />
    }

    // const blockComponentsToWrapInBackground = () => {
    //   return [homepage.blocks[2], homepage.blocks[3]].map((block, index) => {
    //     const { id, blockType, ...componentProps } = block
    //     const Component = sections[blockType] || Fallback
    //     return <Component key={id} {...(componentProps as any)} />
    //   })
    // }

    const otherBlockComponents = () => {
        if (pageData.blocks.length < 3) return <></>

        return [pageData.blocks[2], pageData.blocks[3]].filter(Boolean).map((block) => {
            const { id, blockType, ...componentProps } = block
            const Component = sections[blockType] || Fallback
            return <Component key={id} {...(componentProps as any)} />
        })
    }

    // const HeightDivSmall = useMemo(() => styled.div`
    //   ${dynamicHeroHeight({
    //     breakpointHeight: smallHeroBreakpointHeight,
    //     minHeroHeight: smallHeroMinHeight,
    // })}
    // `, [smallHeroBreakpointHeight, smallHeroMinHeight])

    const firstComponentIsHero = pageData.blocks.length > 0 && pageData.blocks[0].blockType === "ContentfulHomepageHero"

    return (
        <>
            <Stack gap={4}>
                {!firstComponentIsHero ? (
                    <HorizontalSlantContainer id={pageData.id} autoHeight>
                        <Container sx={headerSlantContainerNoContentStyles}></Container>
                    </HorizontalSlantContainer>
                ) : (

                    <HorizontalSlantContainer id={pageData.id} bottomDirection="right">
                        <HeightDiv>
                            <Stack
                                alignItems="stretch"
                                justifyContent="space-around"
                                style={{ height: "100%", color: "white" }}
                            >
                                {firstBlockComponent()}
                            </Stack>
                        </HeightDiv>
                    </HorizontalSlantContainer>
                )}

                {!isWholesalePage && showTitle && (
                    <Container>
                        <Box {...inspectorProps({
                            entryId: pageData.contentful_id,
                            fieldId: 'title',
                        })}>
                            <Typography variant="h1" gutterBottom>
                                {pageData.title}
                            </Typography>
                        </Box>
                    </Container>
                )}

                <Box {...inspectorProps({
                    entryId: pageData.contentful_id,
                    fieldId: 'content',
                })}>
                    {!firstComponentIsHero && <Container>{firstBlockComponent()}</Container>}

                    {!showTitle ? secondBlockComponent() : <Container sx={{ marginBottom: 10 }}>{secondBlockComponent()}</Container>}

                    <Box>{otherBlockComponents()}</Box>

                    {/* <Box sx={styles.backgroundWrapComponent}>
            {blockComponentsToWrapInBackground()}
          </Box> */}

                    {pageData.blocks
                        .map((block, index) => {
                            if (index > 3) {
                                const { id, blockType, ...componentProps } = block
                                const Component = sections[blockType] || Fallback

                                if (blockType === "ContentfulHomepageHero") {
                                    return (
                                        <HorizontalSlantContainer
                                            id={id}
                                            key={id}
                                            breakpointHeight={smallHeroBreakpointHeight}
                                            minHeroHeight={smallHeroMinHeight}
                                        >
                                            <HeightDivSmall>
                                                <Stack
                                                    alignItems="center"
                                                    justifyContent="center"
                                                    sx={{
                                                        height: "100%",
                                                        color: "white",
                                                        pt: { xs: 1, sm: 1, md: 3, lg: 4 },
                                                        pb: { xs: 1, sm: 1, md: 3, lg: 4 },
                                                    }}
                                                >
                                                    <Component {...(componentProps as any)} />
                                                </Stack>
                                            </HeightDivSmall>
                                        </HorizontalSlantContainer>
                                    )
                                } else {
                                    return <Component key={id} {...(componentProps as any)} />
                                }
                            }

                            return null
                        })
                        .filter((e) => e != null)}
                </Box>
            </Stack>
        </>
    )
}